<template>
  <!--  eslint-disable -->
  <div
    :class="[
      'inno-line-item px-0 py-4 border-b border-[--color-gray-line] lg:first-of-type:pt-0 shadow-none',
      {
        'inno-line-item--removable': removable,
        'inno-line-item--disabled': disabled,
        'inno-line-item--selected': selected,
        'inno-line-item--deleted': deleted,
      },
    ]"
    data-test-id="line-item"
    @keydown="changeFocus"
  >
    <div v-if="$slots.before" class="inno-line-item__before">
      <slot name="before" />
    </div>

    <div class="inno-line-item__main lg:gap-6">
      <VcCheckbox
        v-if="selectable"
        v-model="isSelected"
        class="inno-line-item__checkbox"
        :name="$t('ui_kit.labels.toggle_vendor_select')"
        test-id="vc-line-item-checkbox"
        @change="$emit('select', isSelected)"
      />
      <!-- :disabled="disabled" -->

      <!--  IMAGE -->
      <div v-if="$slots['after-image'] || withImage" class="inno-line-item__img-container">
        <!--  IMAGE -->
        <VcImage
          v-if="withImage"
          class="inno-line-item__img border-none sm:w-[132px]"
          :src="imageUrl"
          :alt="name"
          size-suffix="sm"
          lazy
        />

        <div class="inno-line-item__img-actions">
          <slot name="after-image" />
        </div>
      </div>

      <div
        :class="[
          'inno-line-item__content',
          {
            'inno-line-item__content--with-image': withImage,
            'inno-line-item__content--selectable': !withImage && selectable,
          },
        ]"
      >
        <div class="inno-line-item__name-container">
          <VcProductTitle
            class="inno-line-item__name text-lg"
            :disabled="disabled || deleted"
            :to="route"
            :title="name"
            :target="browserTarget"
            :lines-number="isCart ? 9 : 3"
            @click="$emit('linkClick')"
          >
            {{ name }}
            <div class="text-sm font-normal mt-1 clear-both">
              <span v-if="sku">{{ sku }}</span>
              <span v-if="sku && material" class="mx-1">|</span>
              <span v-if="material"> {{ material }} </span>
            </div>
          </VcProductTitle>

          <div class="vc-line-item__name-actions">
            <slot name="after-title" />
          </div>
        </div>

        <div
          v-if="withProperties || withPrice"
          :class="[
            'inno-line-item__properties',
            {
              'inno-line-item__properties--wide': !withPrice,
              'inno-line-item__properties--hide': !withProperties && withPrice,
            },
          ]"
        >
          <template v-if="withProperties">
            <VcProperty
              v-for="property in properties"
              :key="property.name"
              :label="property.label!"
              :disabled="disabled || deleted"
            >
              {{ property.value }}
            </VcProperty>
          </template>

          <VcProperty v-if="vendor" :label="$t('ui_kit.labels.vendor')" :disabled="disabled">
            {{ vendor.name }}
          </VcProperty>

          <!-- <VcProperty
            v-if="withPrice && !deleted"
            class="inno-line-item__property-price"
            :label="$t('ui_kit.labels.price_per_item')"
            :disabled="disabled"
          >
            <InnoProductPrice
              :list-price="actualPrice || listPrice"
              :disabled="disabled"
              :show-currency-symbol="showCurrencySymbol"
              :price-unit-text="priceUnitText"
              truncate
            />
          </VcProperty> -->
        </div>

        <InnoProductPrice
          v-if="withPrice"
          class="inno-line-item__price text-xl"
          :list-price="listPrice"
          :actual-price="showPlacedPrice ? actualPrice : listPrice"
          :show-currency-symbol="showCurrencySymbol"
          :disabled="disabled"
          :price-unit-text="priceUnitText"
          :sales-unit-text="salesUnitText"
          :zero-price-display="zeroPriceDisplay"
          :align="isMobile ? 'start' : 'end'"
        />

        <div class="inno-line-item__mobile-row">
          <div class="inno-line-item__slot" :class="{ 'inno-line-item__slot--quantity-operators': quantityOperators }">
            <slot />
          </div>
          <InnoProductPrice
            v-if="withTotal"
            class="inno-line-item__total text-lg lg:text-2xl min-w-20 w-auto"
            :list-price="showPlacedPrice ? total : listTotal"
            :actual-price="total"
            align="end"
            :disabled="disabled"
            :show-currency-symbol="showCurrencySymbol"
            :zero-price-display="zeroPriceDisplay"
            truncate
          />
        </div>

        <VcButton
          v-if="removable"
          :aria-label="$t('ui_kit.buttons.remove_from_cart')"
          class="inno-line-item__remove-button text-[--color-body-text] hover:text-[--color-link] mt-1 mr-1 sm:m-0 lg:m-0"
          color="neutral"
          size="lg"
          variant="no-background"
          icon="cart-delete"
          :disabled="disabled"
          @click="$emit('remove')"
        />
      </div>
    </div>

    <div v-if="$slots.after" class="inno-line-item__after">
      <slot name="after" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watchEffect, computed } from "vue";
import type { Property, MoneyType, CommonVendor } from "@/core/api/graphql/types";
import type { RouteLocationRaw } from "vue-router";
import { CUSTOM_PROPERTY_GROUP_MATERIAL } from "@/shared/common/constants";
import { getCustomPropertyValue } from "@/core/utilities/innovadis";
import InnoProductPrice from "@/ui-kit//components/molecules/innovadis/inno-product-price.vue";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

interface IEmits {
  (event: "remove"): void;
  (event: "select", value: boolean): void;
  (event: "linkClick"): void;
}

interface IProps {
  imageUrl?: string;
  name: string;
  route?: RouteLocationRaw;
  properties?: Property[];
  listPrice?: MoneyType;
  actualPrice?: MoneyType;
  total?: MoneyType;
  listTotal?: MoneyType;
  selectable?: boolean;
  selected?: boolean;
  removable?: boolean;
  disabled?: boolean;
  deleted?: boolean;
  withImage?: boolean;
  withProperties?: boolean;
  withPrice?: boolean;
  withTotal?: boolean;
  vendor?: CommonVendor;
  browserTarget?: BrowserTargetType;
  showPlacedPrice?: boolean;
  showCurrencySymbol?: boolean;
  withLabel?: boolean;
  quantityOperators?: boolean;
  priceUnitText?: string;
  salesUnitText?: string;
  sku?: string;
  productGroupMaterial?: string;
  highestQuantity?: number;
  zeroPriceDisplay?: string;
  isCart?: boolean;
}

defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  properties: () => [],
  browserTarget: "_blank",
  priceUnitText: "",
  showCurrencySymbol: false,
  withLabel: false,
  quantityOperators: false,
  highestQuantity: 1,
  zeroPriceDisplay: "on-request",
  isCart: false,
});

const isSelected = ref<boolean>(true);

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("lg");

// to ensure all quantity controls have the same width without unnessecary white space
const dynamicQuantityWidthClass = computed(() => {
  let dynamicQuantityWidth = 38;

  switch (countDigits(props.highestQuantity)) {
    case 1: // 1 - 9
      dynamicQuantityWidth = 38;
      break;
    case 2: // 10 - 99
      dynamicQuantityWidth = 45;
      break;
    case 3: // 100 - 999
      dynamicQuantityWidth = 55;
      break;
    case 4: // 1.000 - 9.999
      dynamicQuantityWidth = 60;
      break;
    case 5: // 10.000 - 99.000
      dynamicQuantityWidth = 65;
      break;
    default: // 100.000+
      dynamicQuantityWidth = 70;
      break;
  }

  return `${dynamicQuantityWidth}px`;
});

// to ensure all orderline price displays have the same width without unnessecary white space
// based on quantity (assuming 1 digit quantity, 1-9, being a value between €0.01 and €999.99, or else it will be an odd-ball)
// max
const dynamicOrderlinePriceWidthClass = computed(() => {
  let dynamicOrderlinePriceWidth = 90;

  switch (countDigits(props.highestQuantity)) {
    case 1: // €0.01 - €99.99
      dynamicOrderlinePriceWidth = 90;
      break;
    case 2: // €100.00 - €999.99
      dynamicOrderlinePriceWidth = 102;
      break;
    case 3: // €1.000.00 - €9.999.99
      dynamicOrderlinePriceWidth = 115;
      break;
    case 4:
    case 5: // €10.000.00 - €9.9999.99
      dynamicOrderlinePriceWidth = 128;
      break;
    default: // 100.000+
      dynamicOrderlinePriceWidth = 90;
      break;
  }

  return `${dynamicOrderlinePriceWidth}px`;
});

function countDigits(num: number): number {
  return num.toString().replace("-", "").length;
}

// get group material value from product properties. this value doesnt seem to be passed to the line item
const material = computed(
  () => props.productGroupMaterial ?? getCustomPropertyValue(props.properties, CUSTOM_PROPERTY_GROUP_MATERIAL),
);

function changeFocus(event: KeyboardEvent) {
  const { target: targetElement, currentTarget } = event;
  if (!(currentTarget instanceof HTMLElement) || !(targetElement instanceof HTMLElement)) {
    return;
  }
  const targetClassList = targetElement.classList;
  let nextElement: Element | null = null;
  switch (event.key) {
    case "ArrowUp":
      nextElement = currentTarget.previousElementSibling;
      break;
    case "ArrowDown":
      nextElement = currentTarget.nextElementSibling;
      break;
  }
  if (nextElement && nextElement.classList.contains("inno-line-item") && nextElement instanceof HTMLElement) {
    const nextTargetElement = nextElement.querySelector(`.${[...targetClassList].join(".")}`);
    (nextTargetElement as HTMLElement | null)?.focus();
  }
}

watchEffect(() => {
  isSelected.value = props.selected;
});
</script>

<style lang="scss">
.inno-line-item {
  $selected: "";
  $removable: "";
  $removed: "";
  $deleted: "";
  $disabled: "";

  --bg-color: var(--color-additional-50);

  @apply relative flex flex-col gap-2 p-3 rounded border shadow-md bg-[--bg-color];

  @container (width > theme("containers.2xl")) {
    @apply p-4 rounded-none border-0 shadow-none;
  }

  &--selected {
    $selected: &;
    --bg-color: var(--color-secondary-50);
  }

  &--removable {
    $removable: &;
  }

  &--deleted {
    $deleted: &;
  }

  &--removed {
    $removed: &;
  }

  &--disabled {
    $disabled: &;
  }

  &__before,
  &__after {
    &:empty {
      @apply hidden;
    }
  }

  &:first-child {
    @container (width > theme("containers.2xl")) {
      @apply rounded-t-[inherit];
    }
  }

  &:last-child {
    @container (width > theme("containers.2xl")) {
      @apply rounded-b-[inherit];
    }
  }

  &__main {
    // Innovadis custom: On mobile align content to the top so image and remove button start at the same vertical position.
    @apply flex items-start min-h-[1.25rem];

    @container (width > theme("containers.2xl")) {
      // Innovadis custom: Keep desktop row layout full-width and vertically centered.
      @apply w-full items-center;
    }
  }

  &__checkbox {
    @apply flex-none z-[1] absolute top-0.5 left-0.5 p-2 rounded bg-[--bg-color];

    @container (width > theme("containers.2xl")) {
      @apply static top-auto left-auto -m-2;
    }
  }

  &__img-container {
    // Innovadis custom: Reserve a fixed image column width/height for consistent row layout.
    @apply flex flex-col shrink-0 size-16;

    @container (width > theme("containers.2xl")) {
      @apply size-12;
    }

    @container (width > theme("containers.4xl")) {
      @apply size-16;
    }
  }

  &__img {
    @apply shrink-0 size-16 rounded border object-contain object-center;

    @container (width > theme("containers.2xl")) {
      @apply size-12;
    }

    @container (width > theme("containers.4xl")) {
      @apply size-16;
    }

    #{$disabled} &,
    #{$deleted} & {
      @apply opacity-50;
    }
  }

  &__content {
    @apply w-full;

    @container (width > theme("containers.2xl")) {
      @apply contents;
    }

    &--with-image {
      @apply w-[calc(100%-theme(width.16))] ps-3;
    }

    &--selectable {
      @apply ps-8;
    }
  }

  &__name {
    // Innovadis custom: Slightly tighter title line-height for compact cart rows.
    @apply text-sm leading-tight min-w-0;

    @container (width > theme("containers.2xl")) {
      @apply grow min-h-0;
    }

    #{$removable} & {
      @apply pr-12;

      @container (width > theme("containers.2xl")) {
        @apply pr-0;
      }
    }

    .vc-product-title__text {
      // Innovadis custom: Apply tighter line-height to the real title text element rendered by VcProductTitle.
      @apply text-[--color-body-text] leading-tight !important;
    }
  }

  &__name-container {
    @apply min-w-0;

    @container (width > theme("containers.2xl")) {
      // Innovadis custom: Let the title column absorb free space so trailing columns stay aligned.
      @apply flex-1 min-w-0;
    }
  }

  &__properties {
    @apply flex-none mt-3 mr-3;

    @container (width > theme("containers.2xl")) {
      @apply mt-0 w-40 mr-0;
    }

    @container (width > theme("containers.4xl")) {
      @apply w-[11.875rem];
    }

    &--wide {
      @container (width > theme("containers.4xl")) {
        @apply w-[15.5rem];
      }
    }

    &--hide {
      @apply hidden;
    }
  }

  &__property-price {
    @container (width > theme("containers.4xl")) {
      @apply hidden;
    }

    * {
      @apply leading-[inherit] #{!important};
    }
  }

  &__price {
    --vc-product-price-font-size: theme(fontSize.sm);

    @container (width <= theme("containers.4xl")) {
      @apply mt-4;
    }

    @container (width > theme("containers.2xl")) {
      // Innovadis custom: Lock desktop unit-price column width for consistent vertical column alignment.
      @apply shrink-0 w-[8.25rem] mt-0 text-end;
    }

    @container (width > theme("containers.4xl")) {
      @apply w-[8.25rem];
    }

    #{$deleted} & {
      @container (width > theme("containers.4xl")) {
        @apply invisible;
      }
    }

    .vc-product-price__price-unit {
      @apply text-base font-normal;
    }

    .vc-product-price__sales-unit {
      @apply text-xxs font-normal text-[--color-neutral-500] leading-[1.5rem];
    }
  }

  &__mobile-row {
    @apply flex md:contents;

    @container (width <= theme("containers.2xl")) {
      @apply flex items-center gap-3 mt-3;
    }
  }

  &__slot {
    @apply flex-none empty:hidden;

    @container (width > theme("containers.2xl")) {
      // Innovadis custom: Prevent quantity/action slot from shrinking so rows keep the same column structure.
      @apply shrink-0;
    }

    &:has(.vc-add-to-cart--hide-button, * .vc-add-to-cart--hide-button) {
      @apply w-[6.5rem];
    }

    // Innovadis custom
    input[type="number"] {
      min-width: v-bind(dynamicQuantityWidthClass);
    }

    &:has(
      .add-to-cart,
      * .add-to-cart,
      .vc-add-to-cart:not(.vc-add-to-cart--hide-button),
      * .vc-add-to-cart:not(.vc-add-to-cart--hide-button),
      .vc-product-button
    ) {
      @apply w-full;

      @container (width > theme("containers.md")) {
        @apply w-72;
      }

      @container (width > theme("containers.2xl")) {
        @apply w-48;
      }

      @container (width > theme("containers.3xl")) {
        @apply w-[15.7rem];
      }
    }

    #{$deleted} & {
      @container (width <= theme("containers.2xl")) {
        @apply hidden;
      }

      @container (width > theme("containers.2xl")) {
        @apply invisible;
      }
    }
  }

  &__total {
    --vc-product-price-font-size: theme(fontSize.sm);

    @apply w-full min-w-0;

    .vc-product-price__actual {
      @apply h-full flex align-middle;
    }

    @container (width > theme("containers.2xl")) {
      --vc-product-price-font-size: theme(fontSize.sm);

      @apply shrink-0 w-[6.5rem];

      // innovadis custom
      .vc-product-price__actual {
        min-width: v-bind(dynamicOrderlinePriceWidthClass);
      }
    }

    @container (width > theme("containers.4xl")) {
      --vc-product-price-font-size: theme(fontSize.base);

      @apply w-[8.625rem];
    }
  }

  &__remove-button {
    @container (width > theme("containers.2xl")) {
      // Innovadis custom: Keep remove action in a fixed column on desktop.
      @apply flex-none;
    }

    span {
      @apply size-12;
    }

    @container (width <= theme("containers.2xl")) {
      @apply top-0.5 right-0.5 absolute #{!important};
    }

    @container (width > theme("containers.2xl")) {
      @apply -my-2 -me-2;
    }
  }
}
</style>
